import { useEffect, useMemo, useState } from "react";
import { openWithCacheBuster } from "./cacheBuster";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	AlertTriangle,
	ArrowRight,
	ExternalLink,
	MapPin,
	Trophy,
	Wrench,
} from "lucide-react";
import type {
	KeywordCannibalizationDetails,
	PageIssue,
	ProposedFix,
} from "@/types/technicalAudit";
import {
	hasLocationSignals,
	isGeoCannibalization,
	sameUrl,
	suggestWinner,
} from "./cannibalizationWinner";

interface KeywordCannibalizationCardProps {
	issue: PageIssue;
	// Emits the apply payload ({ winner_url }) for the drawer's Apply button, or
	// null when there's nothing safe to apply on THIS page (it's the winner, or
	// the group is a geo false-positive).
	onEdit?: (value: ProposedFix | null) => void;
}

// keyword_cannibalization: multiple pages target the same primary keyword.
// True duplicates get a winner picker → the loser's canonical points at the
// winner (the backend canonical-write apply). Location (geo) pages that only
// LOOK like duplicates are routed to differentiate/dismiss, because merging
// them would delete legitimate per-city pages.
export function KeywordCannibalizationCard({
	issue,
	onEdit,
}: KeywordCannibalizationCardProps) {
	const details = (issue.issue_details ||
		{}) as Partial<KeywordCannibalizationDetails>;
	// Memoize so the arrays keep a stable identity across renders (keeps the
	// useMemo/useEffect deps below from re-running every render).
	const competingUrls = useMemo(
		() => (Array.isArray(details.competing_urls) ? details.competing_urls : []),
		[details.competing_urls],
	);
	const competingTitles = useMemo(
		() =>
			Array.isArray(details.competing_titles) ? details.competing_titles : [],
		[details.competing_titles],
	);
	const competingCount =
		typeof details.competing_count === "number"
			? details.competing_count
			: competingUrls.length;

	const thisPage = issue.page_url;

	// Prefer backend enrichment; fall back to client-side heuristics so the card
	// works on existing audit runs.
	const isGeo = useMemo(
		() =>
			typeof details.is_geo_pattern === "boolean"
				? details.is_geo_pattern
				: isGeoCannibalization(competingUrls),
		[details.is_geo_pattern, competingUrls],
	);
	const suggested = useMemo(
		() => details.suggested_winner ?? suggestWinner(competingUrls) ?? "",
		[details.suggested_winner, competingUrls],
	);
	// Not confidently geo, but some pages carry location hints — warn before the
	// reviewer canonicalizes a group that might be per-city pages.
	const showLocationWarning = useMemo(
		() => !isGeo && hasLocationSignals(competingUrls),
		[isGeo, competingUrls],
	);

	const [selectedWinner, setSelectedWinner] = useState<string>(suggested);
	useEffect(() => {
		setSelectedWinner(suggested);
	}, [suggested]);

	// This page is a "loser" only when a different page is the chosen winner.
	const thisPageIsWinner =
		!!selectedWinner && sameUrl(selectedWinner, thisPage);

	// Sync the apply payload. Only emit a winner when THIS page is a loser of a
	// real (non-geo) cannibalization group — otherwise clear it so Apply stays a
	// safe no-op (the backend returns needs_input).
	useEffect(() => {
		if (!onEdit) return;
		if (!isGeo && selectedWinner && !thisPageIsWinner) {
			onEdit({ winner_url: selectedWinner } as ProposedFix);
		} else {
			onEdit(null);
		}
	}, [onEdit, isGeo, selectedWinner, thisPageIsWinner]);

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border border-coral/40 bg-coral-light">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-coral/10">
					<AlertTriangle className="w-5 h-5 text-coral" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-coral-dark">
						Keyword cannibalization detected
					</h4>
					<p className="text-sm mt-1 text-coral-dark">
						{competingCount} page{competingCount === 1 ? "" : "s"} competing for
						the same primary keyword.
					</p>
					{details.keyword_signature && (
						<p className="text-sm mt-1 text-coral-dark break-words">
							Keyword:{" "}
							<code className="rounded bg-coral/10 px-1 py-0.5">
								{details.keyword_signature}
							</code>
						</p>
					)}
				</div>
			</div>

			{isGeo ? (
				// Geo false-positive: same service phrase, different cities. Merging
				// these would remove legitimate location pages — so no winner picker.
				<Card className="border-teal/30 shadow-sm bg-teal-light/40">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-teal-dark flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-teal/15 flex items-center justify-center">
								<MapPin className="w-4 h-4 text-teal-dark" />
							</div>
							These look like location pages
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0 space-y-3">
						<div className="p-3 bg-card rounded-lg border border-teal/30">
							<p className="text-sm text-teal-dark font-medium">
								The competing pages share a service phrase but target different
								cities. They serve distinct local intent — this is usually not
								true cannibalization.
							</p>
							<p className="text-sm text-teal-dark mt-1">
								Do <strong>not</strong> canonicalize them (that would drop the
								geo pages from search). Instead, differentiate each page's title
								and H1 to lead with its city, or dismiss this finding if the
								intent is already distinct.
							</p>
						</div>
						{competingUrls.length > 0 && (
							<ul className="divide-y divide-border rounded-lg border border-border bg-card">
								{competingUrls.map((url, index) => (
									<li key={url} className="px-3 py-2">
										{competingTitles[index] && (
											<p className="text-sm font-medium text-foreground break-words">
												{competingTitles[index]}
											</p>
										)}
										<a
											href={url}
											onClick={(e) => openWithCacheBuster(e, url)}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-1 text-xs text-coral hover:text-coral-hover break-all"
										>
											<span className="break-all">{url}</span>
											<ExternalLink className="w-3 h-3 shrink-0" />
										</a>
									</li>
								))}
							</ul>
						)}
						<Badge
							variant="outline"
							className="border-teal/40 bg-teal-light text-teal-dark"
						>
							Location pages — review, don't merge
						</Badge>
					</CardContent>
				</Card>
			) : (
				// Real cannibalization: pick the winner; the other pages canonical to it.
				<Card className="border-teal/30 shadow-sm bg-teal-light/40">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-teal-dark flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-teal/15 flex items-center justify-center">
								<Wrench className="w-4 h-4 text-teal-dark" />
							</div>
							Pick the winning page
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0 space-y-3">
						<p className="text-sm text-teal-dark">
							Choose the page that should rank for this keyword. The other
							competing pages get a canonical pointing at it.
						</p>

						{showLocationWarning && (
							<div className="flex items-start gap-2 rounded-lg border border-coral/40 bg-coral-light p-3">
								<MapPin className="w-4 h-4 text-coral-dark mt-0.5 shrink-0" />
								<p className="text-sm text-coral-dark">
									<strong>Heads up:</strong> some of these URLs look like
									different locations. If they target different cities, do{" "}
									<strong>not</strong> canonicalize them — that would drop the
									geo pages from search. Only pick a winner if these are genuine
									duplicates of the same page.
								</p>
							</div>
						)}

						<fieldset className="rounded-lg border border-border bg-card divide-y divide-border">
							{competingUrls.map((url, index) => {
								const isWinner = sameUrl(url, selectedWinner);
								const isThis = sameUrl(url, thisPage);
								const isSuggested = sameUrl(url, suggested);
								return (
									<label
										key={url}
										className={`flex items-start gap-3 px-3 py-2 cursor-pointer ${
											isWinner ? "bg-teal-light/60" : ""
										}`}
									>
										<input
											type="radio"
											name={`cannibal-winner-${issue.id}`}
											className="mt-1 accent-teal"
											checked={isWinner}
											onChange={() => setSelectedWinner(url)}
										/>
										<span className="flex-1 min-w-0">
											{competingTitles[index] && (
												<span className="block text-sm font-medium text-foreground break-words">
													{competingTitles[index]}
												</span>
											)}
											<span className="flex flex-wrap items-center gap-1 text-xs">
												<a
													href={url}
													onClick={(e) => openWithCacheBuster(e, url)}
													target="_blank"
													rel="noopener noreferrer"
													onClick={(e) => e.stopPropagation()}
													className="inline-flex items-center gap-1 text-coral hover:text-coral-hover break-all"
												>
													<span className="break-all">{url}</span>
													<ExternalLink className="w-3 h-3 shrink-0" />
												</a>
												{isThis && (
													<Badge
														variant="outline"
														className="border-coral/40 bg-coral-light text-coral-dark"
													>
														this page
													</Badge>
												)}
												{isSuggested && (
													<Badge
														variant="outline"
														className="border-teal/40 bg-teal-light text-teal-dark"
													>
														suggested
													</Badge>
												)}
												{isWinner && (
													<span className="inline-flex items-center gap-0.5 text-teal-dark font-medium">
														<Trophy className="w-3 h-3" /> winner
													</span>
												)}
											</span>
										</span>
									</label>
								);
							})}
						</fieldset>

						<div className="p-3 bg-card rounded-lg border border-teal/30 text-sm">
							{thisPageIsWinner ? (
								<p className="text-teal-dark">
									This page is the chosen winner — nothing to apply here. Open
									the other competing pages and apply the fix there to point
									them at this page.
								</p>
							) : selectedWinner ? (
								<p className="text-teal-dark inline-flex flex-wrap items-center gap-1">
									Apply will set this page's canonical
									<ArrowRight className="w-3 h-3 shrink-0" />
									<code className="rounded bg-teal/10 px-1 py-0.5 break-all">
										{selectedWinner}
									</code>
								</p>
							) : (
								<p className="text-teal-dark">
									Select the winning page to enable Apply.
								</p>
							)}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
